var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { SDKS, } from '../types/types';
import { maskDefaultFieldsForLogging } from '../utils/log-utils';
import { getHubEnvironment, getRawDecodedLinkTokenPayload, getSemverString, isStaging, } from '../utils';
import { toCamelCase } from '../utils/case-conversion';
export var shouldHaveRumSessionReplay = function () {
    return getHubEnvironment() === 'production';
};
export function getContext() {
    var _a;
    return ((_a = datadogLogs.getGlobalContext) === null || _a === void 0 ? void 0 : _a.call(datadogLogs)) || {};
}
export function setContextValue(valueObj) {
    var _a;
    for (var _i = 0, _b = Object.entries(valueObj); _i < _b.length; _i++) {
        var _c = _b[_i], k = _c[0], v = _c[1];
        (_a = datadogLogs.setGlobalContextProperty) === null || _a === void 0 ? void 0 : _a.call(datadogLogs, k, v);
    }
}
export function getTimeSinceOpen() {
    return (Date.now() - getContext().initTime) / 1000;
}
export function setImpressionCounts(surface) {
    var _a;
    var _b = getContext().impressionCounts, impressionCounts = _b === void 0 ? {} : _b;
    var currentCount = impressionCounts[surface] || 0;
    Object.assign(impressionCounts, (_a = {}, _a[surface] = currentCount + 1, _a));
    setContextValue({ impressionCounts: impressionCounts });
}
/**
 * Adds feature flag to the Datadog context if it is not there yet. This allows us to use
 * Datadog to track feature flags that are computed dynamically (not on modal start up).
 *
 * @param featureFlagKebabCase - The raw feature flag name from Statsig. This feature flag
 * is usually in kebab-case in Statsig, however, it will be converted to camel case here
 * to conform to the other feature flags in the `featureFlags` array.
 */
export function addFeatureFlag(featureFlagKebabCase) {
    var featureFlagCamelCase = toCamelCase(featureFlagKebabCase);
    var _a = getContext().featureFlags, featureFlags = _a === void 0 ? [] : _a;
    if (!featureFlags.includes(featureFlagCamelCase))
        setContextValue({
            featureFlags: featureFlags.concat(featureFlagCamelCase),
        });
}
/**
 * Removes feature flag to the Datadog context if it is not there yet. Clean up for `addFeatureFlag`.
 *
 * @param featureFlagKebabCase - The raw feature flag name from Statsig. This feature flag
 * is usually in kebab-case in Statsig, however, it will be converted to camel case here
 * to conform to the other feature flags in the `featureFlags` array.
 */
export function removeFeatureFlag(featureFlagKebabCase) {
    var featureFlagCamelCase = toCamelCase(featureFlagKebabCase);
    var featureFlags = getContext().featureFlags;
    setContextValue({
        featureFlags: featureFlags.filter(function (x) { return x !== featureFlagCamelCase; }),
    });
}
export function addArrayToContextFromObjectByTruthiness(key, value) {
    datadogLogs.addLoggerGlobalContext(key, Object.entries(value).reduce(function (acc, _a) {
        var k = _a[0], v = _a[1];
        return (v ? __spreadArray(__spreadArray([], acc, true), [v === true ? k : "".concat(k, "_").concat(v)], false) : acc);
    }, []));
}
export function updateEmployerOrPlatform(object) {
    setContextValue({ employerOrPlatform: object });
}
export function setCurrentJob(currentJob) {
    setContextValue({
        currentJob: currentJob,
        currentJobStep: {
            stepNumber: 1,
            stepAttempt: 1,
            dontIncrementOnResponse: true,
        },
    });
}
export function incrementCurrentJobStepNumber(responseType) {
    var _a = getContext().currentJobStep, currentJobStep = _a === void 0 ? {
        stepNumber: 1,
        stepAttempt: 1,
        dontIncrementOnResponse: true,
    } : _a;
    var stepNumber = currentJobStep.stepNumber, stepAttempt = currentJobStep.stepAttempt, dontIncrementOnResponse = currentJobStep.dontIncrementOnResponse;
    if (dontIncrementOnResponse) {
        setContextValue({
            currentJobStep: __assign(__assign({}, currentJobStep), { dontIncrementOnResponse: false }),
        });
    }
    else if (responseType === 'next') {
        setContextValue({
            currentJobStep: {
                stepNumber: stepNumber + 1,
                stepAttempt: 1,
            },
        });
    }
    else if (responseType === 'next_with_error')
        setContextValue({
            currentJobStep: { stepNumber: stepNumber, stepAttempt: stepAttempt + 1 },
        });
}
export function setUpDatadog(_a, modalSessionId, uniqueUserId) {
    var _b;
    var linkToken = _a.linkToken, sdk = _a.sdk, platform = _a.platform, version = _a.version;
    if (process.env.GIT_HASH) {
        datadogLogs.addLoggerGlobalContext('version', process.env.GIT_HASH);
    }
    var env = isStaging() ? 'staging' : 'production';
    datadogLogs.addLoggerGlobalContext('env', env);
    datadogLogs.addLoggerGlobalContext('hostname', window.location.href);
    var context = {
        modalSessionId: modalSessionId,
        uniqueUserId: uniqueUserId,
        tokenParams: __assign({}, maskDefaultFieldsForLogging(getRawDecodedLinkTokenPayload(linkToken))),
        sdkVersion: getSemverString(version),
    };
    var linkTokenId = context.tokenParams.data.token_id;
    datadogRum.setRumGlobalContext(__assign(__assign({}, context), { linkTokenId: linkTokenId }));
    datadogRum.setUser({
        id: uniqueUserId,
        modalSessionId: modalSessionId,
        linkTokenId: linkTokenId,
        uniqueUserId: uniqueUserId,
    });
    datadogLogs.addLoggerGlobalContext('metadata', { context: context });
    datadogLogs.addLoggerGlobalContext('visibilityState', document.visibilityState);
    if (!sdk)
        return;
    // Handle setting SDK and Platform in Datadog global context
    if (!SDKS.includes(sdk))
        throw new Error("`sdk` must be one of ".concat(SDKS));
    datadogLogs.addLoggerGlobalContext('browserLanguage', (_b = window.navigator) === null || _b === void 0 ? void 0 : _b.language);
    datadogLogs.addLoggerGlobalContext('sdk', sdk);
    if (sdk === 'react native' || sdk === 'flutter') {
        datadogLogs.addLoggerGlobalContext('platform', platform);
        if (!['ios', 'android'].includes(platform)) {
            datadogLogs.logger.error("\"platform\" must be provided as \"ios\" or \"android\" when sdk is ".concat(sdk));
        }
    }
    else if (sdk === 'react' || sdk === 'standalone-site') {
        datadogLogs.addLoggerGlobalContext('platform', 'web');
    }
    else {
        datadogLogs.addLoggerGlobalContext('platform', sdk);
    }
}
